import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportError } from '@/lib/sentry'
import { isChunkLoadError, recoverFromChunkLoadError } from '@/lib/chunkLoadReload'
import './styles/ErrorBoundary.css'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportError(error, { componentStack: errorInfo.componentStack ?? undefined })

    if (isChunkLoadError(error)) {
      recoverFromChunkLoadError()
    }
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h1 className="error-boundary__title">
            Something went wrong.
          </h1>
          <p className="error-boundary__copy">
            Sorry about that — our team has been notified. Try reloading the page.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="error-boundary__button"
          >
            Reload
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
